/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Alert, Button, Cell, DateRange, FormControl, Grid, Heading, Icon, Text, VFlow } from 'bold-ui'
import CheckPermission from 'components/auth/CheckPermission'
import { useAcessoLotacaoOrEstagio } from 'components/auth/useAcessoLotacao'
import { CheckboxField } from 'components/form'
import DateRangeField from 'components/form/field/DateRangeField'
import {
  CiapCidSelectField,
  CiapCidSelectModel,
  isCiap,
} from 'components/form/field/select/CiapCidSelectField/CiapCidSelectField'
import { EquipeResponsavelField } from 'components/form/field/select/EquipeResponsavelSelectField/EquipeResponsavelField'
import { GrupoCondicaoSelectField } from 'components/form/field/select/GrupoCondicaoSelectField'
import { MicroareaSelectField } from 'components/form/field/select/MicroareaSelectField/MicroareaSelectField'
import { useFlags } from 'config/useFlagsContext'
import { startOfDay, subMonths } from 'date-fns'
import { FormApi } from 'final-form'
import { AcompanhamentoCondicaoSaudeTematicaEnum } from 'graphql/types.generated'
import { useFirebase } from 'hooks/firebase/useFirebase'
import { useServerTime } from 'hooks/useServerTime'
import { Fragment, useEffect, useState } from 'react'
import { FormRenderProps } from 'react-final-form'
import { Route, Switch, useHistory, useRouteMatch } from 'react-router'
import Permissions from 'types/Permissions'
import { isUndefinedOrNull } from 'util/checks'
import { metaPath } from 'util/metaPath'
import { isEmpty } from 'util/validation/Util'

import { FaixaEtariaFilterGroup } from '../components/FaixaEtariaFilterGroup'
import { SexoIdentidadeGeneroButtonGroup } from '../components/SexoIdentidadeGeneroButtonGroup'
import { FILTROS_AVANCADOS_URL, FiltrosAvancadosModal } from './components/FiltrosAvancadosModal'
import { ListaTematicaSection } from './components/lista-tematica/ListaTematicaSection'
import { VulnerabilidadeFilterGroup } from './components/VulnerabilidadeFilterGroup'
import {
  AcompanhamentoCondicaoSaudeFormModel,
  filterDefaultAcompanhamentoCondicaoSaude,
  IvcfFilterModel,
} from './model/model-acompanhamentoCondicaoSaude'
import { SearchFooter } from './SearchFooter'
import { isFilterEqualDefault } from './utils-acompanhamentoCondicaoSaude'

interface AcompanhamentoCondicaoSaudeFormProps extends FormRenderProps<AcompanhamentoCondicaoSaudeFormModel> {
  setListaTematica: (listaTematica: AcompanhamentoCondicaoSaudeTematicaEnum) => void
}

const path = metaPath<AcompanhamentoCondicaoSaudeFormModel>()

const onClearField = (form: FormApi<AcompanhamentoCondicaoSaudeFormModel>, field: string) => {
  form.change(field, undefined)
  form.blur(field)
}

export function AcompanhamentoCondicaoSaudeForm(props: AcompanhamentoCondicaoSaudeFormProps) {
  const { values, form, initialValues, handleSubmit, setListaTematica } = props
  const { PILOTO_LISTA_TEMATICA_ENABLED } = useFlags()
  const { acesso, hasAuthorization } = useAcessoLotacaoOrEstagio()
  const { analytics } = useFirebase()
  const { getServerTimeNow } = useServerTime()
  const now = getServerTimeNow()

  const history = useHistory()
  const match = useRouteMatch()
  const [counterFiltrosAvancadosAplicados, setCounterFiltrosAvancadosAplicados] = useState(0)

  const isCoordenador = hasAuthorization(Permissions.acompanhamentos.condicoesDeSaude.todasAsEquipes)
  const canCheckApenasProblemasCondicoesAtivas = isEmpty(values.grupoCondicao) && isEmpty(values.problemasCondicoes)

  // TODO (Royal Flush): Verificar aplicação dessa lógica no initialValues do Form
  const [dateRange] = useState<DateRange>({
    startDate: subMonths(startOfDay(now), 36),
    endDate: startOfDay(now),
  })

  const isFilterDefault = isFilterEqualDefault(values)

  const handleEquipeResponsavelChange = () => {
    form.change(path.microareas.absolutePath(), null)
  }

  form.change('unidadeResponsavelId', acesso?.unidadeSaude?.id)
  form.change('equipeResponsavelId', acesso?.equipe?.id)
  form.change('isCoordenador', isCoordenador)

  const handleOnSubmit = () => {
    const hasPeriodoUltimoAtendimento =
      values?.periodoUltimoAtendimento?.startDate || values?.periodoUltimoAtendimento?.endDate

    const hasCiap = !isEmpty(values?.problemasCondicoes?.find((item) => isCiap(item)))
    const hasCid = !isEmpty(values?.problemasCondicoes?.find((item) => !isCiap(item)))
    const hasIvcf = !isUndefinedOrNull(values?.ivcf)

    !isEmpty(values.grupoCondicao) && analytics.logEvent('buscar_acomp_condicoes_saude_with_grupo_condicao')
    hasCiap && analytics.logEvent('buscar_acomp_condicoes_saude_with_ciap')
    hasCid && analytics.logEvent('buscar_acomp_condicoes_saude_with_cid')
    hasPeriodoUltimoAtendimento && analytics.logEvent('buscar_acomp_condicoes_saude_with_periodo_ultimo_atend')

    values.ativoListaProblema
      ? analytics.logEvent('buscar_acomp_condicoes_saude_with_condicoes_ativas')
      : analytics.logEvent('buscar_acomp_condicoes_saude_with_condicoes_ativas_e_inativas')

    hasIvcf && analytics.logEvent('buscar_acomp_condicoes_saude_with_ivcf')

    setListaTematica(values?.listaTematica)
    handleSubmit()
  }

  const handleChangeCiapCid = (ciapCidList: CiapCidSelectModel[]) => {
    if (isEmpty(ciapCidList) && isEmpty(values.grupoCondicao)) {
      form.change(path.ativoListaProblema.absolutePath(), false)
    }
  }

  const handleChangeListaTematica = (listaSelecionada: AcompanhamentoCondicaoSaudeTematicaEnum) => {
    if (listaSelecionada !== AcompanhamentoCondicaoSaudeTematicaEnum.GERAL) {
      clearModalFilterValues()
      form.change(path.tria.absolutePath(), undefined)
      form.change(path.tipoVulnerabilidadeSelected.absolutePath(), undefined)
    }

    const pathListaTematica = path.listaTematica.absolutePath()
    form.change(pathListaTematica, listaSelecionada)
  }

  const handleResetFilterToTematicaDefault = () => {
    const filtersDefault = filterDefaultAcompanhamentoCondicaoSaude[values.listaTematica] ?? initialValues
    form.change('', { ...values, ...filtersDefault })
  }

  const formReset = () => {
    form.reset({ listaTematica: values.listaTematica, ...props.initialValues })
    handleChangeListaTematica(values.listaTematica)
  }

  const handleFiltrosAvancadosClick = () => {
    history.push(`${match.url}${FILTROS_AVANCADOS_URL}`)
  }

  const applyModalFilterValues = (formValues: IvcfFilterModel) => {
    form.change(path.ivcf?.exibirCidadaoIdosoSemIvcf.absolutePath(), formValues.exibirCidadaoIdosoSemIvcf)
    form.change(path.ivcf?.pontuacao.absolutePath(), formValues.pontuacao)
    form.change(path.ivcf?.riscoVulnerabilidade.absolutePath(), formValues.riscoVulnerabilidade)
    form.change(path.ivcf?.subgrupo.absolutePath(), formValues.subgrupo)
  }

  const clearModalFilterValues = () => {
    form.change(path.ivcf.absolutePath(), undefined)
  }

  const filtrosAvancadosInitialValues: IvcfFilterModel = {
    exibirCidadaoIdosoSemIvcf: values.ivcf?.exibirCidadaoIdosoSemIvcf,
    buscaPorPontuacao: !isUndefinedOrNull(values.ivcf?.pontuacao),
    pontuacao: values.ivcf?.pontuacao,
    riscoVulnerabilidade: values.ivcf?.riscoVulnerabilidade,
    subgrupo: values.ivcf?.subgrupo,
  }

  useEffect(() => {
    setCounterFiltrosAvancadosAplicados(
      (values.ivcf?.exibirCidadaoIdosoSemIvcf ? 1 : 0) +
        (values.ivcf?.pontuacao?.firstValue ? 1 : 0) +
        (values.ivcf?.pontuacao?.secondValue ? 1 : 0) +
        (values.ivcf?.riscoVulnerabilidade?.length ?? 0) +
        (values.ivcf?.subgrupo?.length ?? 0)
    )
  }, [values.ivcf])

  return (
    <VFlow vSpacing={2}>
      <Grid wrap>
        <Cell size={3} style={styles.cell}>
          <FormControl label='Unidade responsável'>
            <Text>{acesso?.unidadeSaude?.nome.titleCase()}</Text>
          </FormControl>
        </Cell>
        <Cell size={isCoordenador ? 4 : 3} style={styles.cell}>
          <EquipeResponsavelField
            name={path.equipeResponsavel}
            equipe={acesso?.equipe}
            unidadeSaudeId={values?.unidadeResponsavelId}
            hasAccessToTodasEquipes={isCoordenador}
            placeholder='Selecione uma equipe'
            required={isCoordenador}
            onClear={() => onClearField(form, path.equipeResponsavel.absolutePath())}
            onChange={handleEquipeResponsavelChange}
          />
        </Cell>
        <Cell size={3} style={styles.cell}>
          <MicroareaSelectField
            name={path.microareas}
            label='Microárea'
            metaEquipe={path.equipeResponsavel}
            placeholder='Todas as microáreas'
            disabled={isCoordenador && !values?.equipeResponsavel}
          />
        </Cell>
        <Cell size={12}>
          {PILOTO_LISTA_TEMATICA_ENABLED && (
            <ListaTematicaSection
              listaTematica={values.listaTematica}
              handleChangeListaTematica={handleChangeListaTematica}
            />
          )}
        </Cell>
        <CheckPermission permission={Permissions.acompanhamentos.condicoesDeSaude.problemasECondicoes}>
          <Cell size={12}>
            <Heading level={2}>Problemas e condições</Heading>
            <Alert
              inline
              type='info'
              styles={{
                wrapper: css`
                  margin-top: 0.5rem;
                `,
                container: css`
                  margin-right: 5rem;
                `,
              }}
            >
              A busca retorna apenas pessoas que possuem todas as condições selecionadas e ativas na lista de problemas
              ou avaliadas no SOAP e fichas de atendimento individual.
            </Alert>
          </Cell>
        </CheckPermission>
        <Cell size={12}>
          <Grid>
            <CheckPermission permission={Permissions.acompanhamentos.condicoesDeSaude.problemasECondicoes}>
              <Cell
                size={6}
                style={css`
                  margin-top: 0.5rem;
                `}
              >
                <GrupoCondicaoSelectField
                  label='Grupos de condições prioritários'
                  name={path.grupoCondicao.absolutePath()}
                  multiple
                  placeholder='Selecione grupos de condições prioritários'
                />
              </Cell>
              <Cell
                size={6}
                style={css`
                  margin-top: 0.5rem;
                `}
              >
                <CiapCidSelectField
                  label='CIAP2 e CID10'
                  name={path.problemasCondicoes.absolutePath()}
                  multiple
                  placeholder='Selecione outros CIAP2 e CID10'
                  onChange={handleChangeCiapCid}
                />
              </Cell>
              <Cell size={12}>
                <CheckboxField
                  name={path.ativoListaProblema.absolutePath()}
                  label='Buscar apenas problemas / condições ativas na lista de problemas e condições.'
                  disabled={canCheckApenasProblemasCondicoesAtivas}
                />
              </Cell>
            </CheckPermission>
            <Cell size={12}>
              <Heading level={2}>Informações da pessoa</Heading>
            </Cell>
            <Cell size={6}>
              <SexoIdentidadeGeneroButtonGroup onChange={form.change} meta={path} />
            </Cell>
            <Cell size={6}>
              <DateRangeField
                name={path.periodoUltimoAtendimento.absolutePath()}
                label='Período do último atendimento'
                // Props comentadas estão com erro no bold e não estão funcionando no momento
                // startPlaceholder='Data inicial'
                // finalPlaceholder='Data final'
                initialValue={dateRange}
                maxDate={now}
              />
            </Cell>
            {values.listaTematica === AcompanhamentoCondicaoSaudeTematicaEnum.GERAL && (
              <Fragment>
                <Cell size={12}>
                  <Heading level={4}>Habilitar filtros:</Heading>
                </Cell>
                <Cell size={12}>
                  <VulnerabilidadeFilterGroup
                    meta={path}
                    onChange={form.change}
                    buscaPorPontuacao={values.ivcf?.buscaPorPontuacao}
                  />
                </Cell>
              </Fragment>
            )}
            <Cell size={12}>
              <FaixaEtariaFilterGroup
                change={form.change}
                meta={path}
                faixaEtariaFilter={values?.tipoFaixaEtaria}
                tipoVulnerabilidade={values?.tipoVulnerabilidadeSelected}
              />
            </Cell>
            <Cell size={4} />
            {PILOTO_LISTA_TEMATICA_ENABLED &&
              values.listaTematica === AcompanhamentoCondicaoSaudeTematicaEnum.PESSOA_IDOSA && (
                <Cell size={12}>
                  <Button kind='primary' size='small' skin='outline' onClick={handleFiltrosAvancadosClick}>
                    <Icon icon='filterOutline' />
                    Filtros avançados {counterFiltrosAvancadosAplicados > 0 && `(${counterFiltrosAvancadosAplicados})`}
                  </Button>
                  <Switch>
                    <Route exact path={`${match.url}${FILTROS_AVANCADOS_URL}`}>
                      <FiltrosAvancadosModal
                        initialValues={filtrosAvancadosInitialValues}
                        clearModalFilterValues={clearModalFilterValues}
                        applyModalFilterValues={applyModalFilterValues}
                      />
                    </Route>
                  </Switch>
                </Cell>
              )}
          </Grid>
        </Cell>
      </Grid>
      <SearchFooter
        handleSubmit={handleOnSubmit}
        onCleanFilters={formReset}
        isCoordenador={isCoordenador}
        isListaTematica={PILOTO_LISTA_TEMATICA_ENABLED}
        isFilterDefault={isFilterDefault}
        equipeResponsavelId={values.equipeResponsavelId}
        onResetToDefault={handleResetFilterToTematicaDefault}
      />
    </VFlow>
  )
}

const styles = {
  cell: css`
    margin-top: 1rem;
  `,
}
